import {
  DepositPaymentStatus,
  PaymentStatus,
  PaymentType,
  Payments,
} from '@prisma/client';
import { formatInTimeZone } from 'date-fns-tz';
import {
  getPaymentMethodPhrase,
  getPaymentStatusKey,
} from '../../../../../helpers/payments';
import {
  convertSnakeToTitleCase,
  formatDollarAmount,
} from '../../../../../utils/global/helper';

export type PaymentRowStatusColor = 'success' | 'muted' | 'danger';

export interface PaymentRowStatusDisplay {
  label: string;
  color: PaymentRowStatusColor;
}

export interface PaymentRowContent {
  description: string;
  metaLine: string;
  statusDisplay: PaymentRowStatusDisplay;
}

function describePaymentDescription(payment: Payments): string {
  const amountLabel = formatDollarAmount(payment.amount / 100);

  if (payment.status === PaymentStatus.REFUNDED) {
    return `${amountLabel} refunded`;
  }
  if (
    payment.type === PaymentType.DEPOSIT &&
    payment.depositStatus === DepositPaymentStatus.FORFEITED
  ) {
    return `${amountLabel} deposit kept`;
  }
  if (payment.status === PaymentStatus.VOIDED) {
    return `${amountLabel} voided`;
  }
  if (payment.status === PaymentStatus.FAILED) {
    return `${amountLabel} payment failed`;
  }
  return `${amountLabel} ${getPaymentMethodPhrase(payment.method)}`;
}

function formatPaymentMetaLine(payment: Payments, timezone: string): string {
  const dateLabel = formatInTimeZone(
    payment.createdAt,
    timezone,
    'MMM d, yyyy',
  );
  const timeLabel = formatInTimeZone(payment.createdAt, timezone, 'h:mm a');
  return `${dateLabel} at ${timeLabel}`;
}

function describePaymentStatusDisplay(
  payment: Payments,
): PaymentRowStatusDisplay {
  const statusKey = getPaymentStatusKey(payment);

  switch (statusKey) {
    case 'succeeded':
      return { label: 'Succeeded', color: 'success' };
    case 'forfeited':
      return { label: 'Forfeited', color: 'muted' };
    case 'refunded':
      return { label: 'Refunded', color: 'danger' };
    case null:
      return { label: convertSnakeToTitleCase(payment.status), color: 'muted' };
    default: {
      const exhaustiveCheck: never = statusKey;
      throw new Error(`Unhandled payment status key: ${exhaustiveCheck}`);
    }
  }
}

export function describePaymentRow(
  payment: Payments,
  timezone: string,
): PaymentRowContent {
  return {
    description: describePaymentDescription(payment),
    metaLine: formatPaymentMetaLine(payment, timezone),
    statusDisplay: describePaymentStatusDisplay(payment),
  };
}
